import React from 'react';
import { Navigate } from 'react-router-dom';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  Progress,
  SidebarItem,
  UserIcon,
  WarningPanel,
} from '@backstage/core-components';
import {
  identityApiRef,
  useApi,
  useRouteRef,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';

/** The same path for every user, see `MyProfileRedirect`. */
export const MY_PROFILE_PATH = '/my-profile';

const useMyUserEntity = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  return useAsync(async () => {
    const profile = await identityApi.getBackstageIdentity();
    let entity: Entity | undefined;
    if (profile) {
      entity = await catalogApi.getEntityByRef(profile.userEntityRef);
    }
    return entity;
  }, []);
};

export const MyProfileSidebarItem = () => {
  const { value: entity } = useMyUserEntity();
  // Annonymous or unknown user
  if (!entity) {
    return null;
  }
  return <SidebarItem text="My Profile" to={MY_PROFILE_PATH} icon={UserIcon} />;
};

/**
 * Sends the signed-in user on to their own entity page. The path is the same
 * for everybody, so it can be bookmarked or pinned as a shortcut, and the
 * sidebar can link to it without first working out who is signed in.
 */
export const MyProfileRedirect = () => {
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const { value: entity, loading } = useMyUserEntity();
  if (loading) {
    return <Progress />;
  }
  if (!entity) {
    return (
      <WarningPanel title="No profile">
        There is no user in the catalog for the signed-in identity.
      </WarningPanel>
    );
  }
  return (
    <Navigate
      replace
      to={catalogEntityRoute(parseEntityRef(stringifyEntityRef(entity)))}
    />
  );
};
